import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { saveAs } from 'file-saver';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Paper,
  TableContainer,
  Alert,
  Button,
} from '@mui/material';
import api from '../services/api';

function flatten(obj, prefix = '') {
  let res = {};
  Object.entries(obj || {}).forEach(([key, val]) => {
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      Object.assign(res, flatten(val, newKey));
    } else {
      res[newKey] = val;
    }
  });
  return res;
}

export default function FannotatorAnnotationsPage() {
  const { filename } = useParams();
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const resp = await api.get(`/admin/fannotator/annotations/${filename}`);
        setRecords(resp.data.annotations || []);
        setErrorMsg('');
      } catch (err) {
        console.error(err);
        setErrorMsg('Failed to fetch annotations');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [filename]);

  const usernames = Array.from(new Set(records.map((r) => r.username))).sort();

  const flattened = records.map((r) => ({
    ...flatten(r),
    username: r.username,
    msg_id: r.msg_id,
  }));

  const skip = new Set([
    '_id',
    'filename',
    'msg_id',
    'username',
    'created_at',
    'updated_at',
    'email',
  ]);
  const fieldsSet = new Set();
  flattened.forEach((rec) => {
    Object.keys(rec).forEach((k) => {
      if (!skip.has(k)) fieldsSet.add(k);
    });
  });
  const fields = Array.from(fieldsSet).sort();

  const dataMap = {};
  flattened.forEach((rec) => {
    if (!dataMap[rec.msg_id]) dataMap[rec.msg_id] = {};
    if (!dataMap[rec.msg_id][rec.username]) dataMap[rec.msg_id][rec.username] = {};
    fields.forEach((f) => {
      dataMap[rec.msg_id][rec.username][f] = rec[f];
    });
  });

  const msgIds = Object.keys(dataMap).sort();

  const handleExportCsv = () => {
    const headers = ['Msg ID'];
    fields.forEach((f) => {
      usernames.forEach((u) => headers.push(`${f}_${u}`));
    });

    const rows = msgIds.map((id) => {
      const row = [id];
      fields.forEach((f) => {
        usernames.forEach((u) => {
          row.push(dataMap[id]?.[u]?.[f] ?? '');
        });
      });
      return row;
    });

    const csv = [headers, ...rows]
      .map((r) =>
        r
          .map((cell) => {
            const str = String(cell ?? '');
            return str.includes(',') ? `"${str.replace(/"/g, '""')}"` : str;
          })
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${filename}-annotations.csv`);
  };


  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Annotations for {filename}</Typography>
        <Box>
          <Button variant="outlined" size="small" onClick={handleExportCsv}
                  sx={{ mr: 1 }}>
            Export CSV
          </Button>
        </Box>
      </Box>
      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}
      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader
            sx={{
              '& thead th': {
                bgcolor: 'grey.200',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                fontWeight: 'bold',
              },
              '& tbody tr:nth-of-type(odd)': { bgcolor: 'grey.50' },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2}>Msg ID</TableCell>
                {fields.map((field) => (
                  <TableCell key={field} align="center" colSpan={usernames.length}>
                    {field}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {fields.map((field) =>
                  usernames.map((user) => (
                    <TableCell key={`${field}-${user}`}>{user}</TableCell>
                  ))
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {msgIds.map((msg) => (
                <TableRow key={msg}>
                  <TableCell>{msg}</TableCell>
                  {fields.map((field) =>
                    usernames.map((user) => (
                      <TableCell key={`${msg}-${field}-${user}`}>{String(dataMap[msg]?.[user]?.[field] ?? '')}</TableCell>
                    ))
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
